import { useEffect } from "react";
import { Link } from "react-router-dom";

// The email address people see on the Terms and Privacy pages and in the footer.
// To change it, change it here and nowhere else.
export const CONTACT_EMAIL = "danthemantvchannel@gmail.com";
const UPDATED = "November 2, 2026";
const PRICE = "$15.99";

function Page({ title, testid, children }) {
  // A page opened from the footer should start at the top.
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="max-w-3xl py-4" data-testid={testid}>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-2">{title}</h1>
      <p className="text-sm text-zinc-500 mb-10">Last updated {UPDATED}</p>
      <div className="space-y-4 text-zinc-300 leading-relaxed">{children}</div>
      <p className="mt-12 text-sm text-zinc-500">
        Questions? Email <a className="text-cyan-400 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        {" "}<Link to="/terms" className="text-cyan-400 hover:underline">Terms of Service</Link>
        {" · "}<Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>
      </p>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-display text-2xl font-bold text-white pt-6">{children}</h2>;
const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
);

export function Terms() {
  return (
    <Page title="Terms of Service" testid="terms-page">
      <p>
        Welcome to Dan The Man TV ("we", "us"). We run a site for streaming music and music videos from Texas.
        By using the site or creating an account, you agree to these terms. If you do not agree, please do not use the site.
      </p>

      <H>1. Your account</H>
      <List items={[
        "You need an account to make playlists, save favorites and follow artists. You can watch and listen to free songs without one.",
        "Use a real email address and keep your password private. You are responsible for what happens on your account.",
        "You must be at least 13 years old. One account is for one person. Please do not share your login.",
      ]} />

      <H>2. Free and Premium</H>
      <p>
        The site has no ads. Free accounts get previews of Premium songs and one playlist of up to 10 songs.
        Premium is {PRICE} US dollars per month and includes:
      </p>
      <List items={[
        "Full-length premium songs",
        "The DJ Booth, to mix your own videos and music on your TV",
        "Favorites and Recently Played",
        "Multiple playlists with 100+ songs",
        "Click-and-drag to reorganize songs in playlists",
        "The Quartz look for buttons and bars",
      ]} />
      <p>
        We may add to or change what each plan includes. If a change makes Premium worth less to you, we will tell you before
        it applies to your next payment, and you can cancel first.
      </p>

      <H>3. Payment and renewal</H>
      <p>
        Premium is a monthly subscription. You pay {PRICE} each month, plus sales tax or VAT where it applies. Tax is worked out and
        shown on the Stripe checkout page before you pay. Your subscription renews automatically every month until you cancel.
      </p>
      <p>
        Payments are handled by Stripe, which acts as the seller of record for your purchase, so your receipt may show Stripe or Link
        as the seller. We never see or store your full card number.
      </p>
      <p>If we change the price, we will tell you before it applies to your next payment, and you can cancel first.</p>

      <H>4. Cancelling</H>
      <p>
        You can cancel at any time on your <Link to="/account" className="text-cyan-400 hover:underline">Account</Link> page.
        You keep Premium until the end of the month you have already paid for, and then it does not renew. You can sign up again whenever you like.
      </p>

      <H>5. No refunds</H>
      <p>
        Months that have been charged are not refunded, including part of a month you did not use, unless the law where you live says
        we must. If you think you were charged by mistake, for example twice, email us and we will look into it.
      </p>

      <H>6. Free trials, free months and gifted Premium</H>
      <p>
        Sometimes we give Premium as a gift, a promotion or for an event. A free trial or free month does not ask for a card and
        never turns into a paid subscription by itself. When it ends, your account goes back to free. Premium that we give you for
        free can be removed at any time.
      </p>

      <H>7. How you may use the site</H>
      <p>The site is for your own personal, non-commercial listening and watching. Please do not:</p>
      <List items={[
        "download, record, copy, sell, re-upload or share our music, videos or artwork",
        "get around previews, limits or the Premium paywall, or share a login so other people skip paying",
        "scrape the site, probe it for weaknesses, overload it or interfere with how it works",
        "use the site for anything illegal",
      ]} />

      <H>8. Our content</H>
      <p>
        The music, videos, artwork and text on Dan The Man TV are owned by or licensed to Dan The Man TV and are protected by copyright.
        You get a limited, personal, revocable permission to stream them on the site. Nothing here is sold to you or transferred to you.
        You are welcome to share a link to a song page.
      </p>
      <p>
        <b>Ideas, song requests and suggestions.</b> Premium members can ask for songs, and anyone can send a suggestion. These are ideas only.
        Dan writes and chooses the lyrics and music himself, and every song made by Dan The Man TV is owned by Dan The Man TV, whether or not it
        was inspired by something you sent. By sending an idea, request or suggestion you agree that we may use it, change it, or ignore it, with no
        credit and no payment to you, and that sending it does not create any ownership, partnership or promise to make a song. Please send only your own ideas.
        Do not send lyrics, melodies or other material that belongs to someone else, or ask for copies of other artists' songs.
      </p>

      <H>9. The DJ Booth, parties and files you play</H>
      <p>
        The DJ Booth is for Premium members. You control a screen (a TV, laptop or tablet) from your phone and mix our songs, and videos or music of your
        own. A host can also start a party: friends who open the host's link and sign in with a free login can each take one deck. The host's Premium covers
        our songs for the party, but a friend still needs their own 18+ check for explicit songs.
      </p>
      <List items={[
        "Only play a video or music file that you made or that you have the right to play. When you pick a file in a booth or a party, you are telling us you have that right, and you are responsible for it.",
        "A file you play from your own device is not uploaded to Dan The Man TV and we do not keep a copy. It goes from your device to the screen and stays in that screen page's memory until the page is closed or reloaded. We do not look at it, check it or take responsibility for it.",
        "Do not record, copy or share another person's file, or our songs, from the screen.",
        "The host may turn friends' files off at any time and may take a friend out of the party.",
        `If you own rights to something and think it is being played without your permission, email us at ${CONTACT_EMAIL}. We will look into it, and we may close a booth or party, or an account, that is used for material its owner has no right to play.`,
      ]} />

      <H>10. Closing an account</H>
      <p>
        You can stop using the site at any time. To have your account deleted, email us. We may suspend or close an account that breaks these
        terms. If we close a paid account for breaking the rules, the rest of the paid month is not refunded.
      </p>

      <H>11. The site as it is</H>
      <p>
        We work hard to keep the site running, but we cannot promise it will always be available or error-free. Songs and features can be added,
        changed or removed.
      </p>

      <H>12. Limits on our responsibility</H>
      <p>
        To the extent the law allows, we are not responsible for indirect or consequential losses, and our total responsibility to you for
        anything connected to the site is limited to the amount you paid us in the 12 months before the problem. Nothing here takes away
        rights you have under the law where you live that cannot be waived.
      </p>

      <H>13. Changes to these terms</H>
      <p>
        We may update these terms. The date at the top shows when they last changed. If you keep using the site after a change, you accept the new
        terms. If a change is a big one, we will tell you by email or on the site.
      </p>

      <H>14. Which law applies</H>
      <p>
        These terms are governed by the laws of the State of Texas, USA. Disputes go to the courts in Llano County, Texas, unless your local law says
        you may use the courts where you live.
      </p>
    </Page>
  );
}

export function Privacy() {
  return (
    <Page title="Privacy Policy" testid="privacy-page">
      <p>
        This explains what Dan The Man TV collects, why, and who helps us run the site. In short: we collect what we need to run your account and
        take payments. We do not sell your information, we show no ads, and we use no advertising or tracking services.
      </p>

      <H>What we collect</H>
      <List items={[
        "Account details: your email address, your display name and your password. Your password is stored scrambled, so we cannot read it.",
        "If you sign up for emails without a login: your email address, and when you asked and when you confirmed.",
        "What you do on the site: the songs you play (and when you last played each one and how many times), your favorites, your playlists, the artists you follow, and your plan (free or Premium). The owner of the site can see which songs each member has played. We also count plays to see which songs are popular.",
        "When you visit: the time of your most recent visits, so we can see how often members come back and which emails to send you.",
        "Which pages are opened: each time a page of the site is opened, we add one to a count for that page and that day, marked as a guest, a free member or a Premium member. These counts have no name, email address or IP address in them, and we do not follow what one guest does from page to page. We leave out search engines and other robots, and the owner's own visits.",
        "What you tell us: if you cancel Premium and give a reason, send a suggestion or request a song, we keep what you wrote. If you say something did not work right, or send a suggestion, we also keep which phone or computer and browser you used and the page you were on, to help find the problem. If you ask for a reply, we keep your email address with it.",
        "DJ Booth and parties: while a booth is open we keep its code, which songs or file names are on the decks and the settings (volume, tempo, crossfader), and for a party each friend's first name and which deck they hold. We keep a finished booth's record for about 2 days and then delete it. A friend's first name is shown to the host and to the other friends in the party, not on the screen. A video or music file that you play from your own device is not uploaded or saved by us. Only its name, size and type go through our servers.",
        "Payment details: your payment is handled entirely by Stripe. We never see your full card number. We keep a reference to your Stripe subscription, whether it is active and when it ends.",
        "On your device: your browser keeps a sign-in token and your player settings (autoplay, shuffle, repeat) so the site remembers you. These are needed for the site to work. It also keeps a small marker that only says this browser has been here before, with no number or name in it, so we can count first-time visitors. None of this is used for ads.",
        "Technical logs: like most websites, the servers that run the site keep ordinary logs (such as your IP address, the time and the page requested) for security and fixing problems. We also use your address to slow down people who try to sign up or sign in over and over, which is how we keep out bots.",
      ]} />

      <H>How we use it</H>
      <List items={[
        "to run the site, sign you in and remember your playlists and favorites",
        "to take payments and give you Premium",
        "to send emails you need, such as a password reset link and billing or account notices. If Dan gives you a free week, trial or month of Premium, we also email you a short note to tell you, and a reminder two days before it ends.",
        "to send you one welcome email with tips on using the site, a few minutes after you make a login and confirm your email",
        "to email you about songs: when Dan adds a new song or features a song, and a monthly roundup of the new songs. If you have not visited for more than a month, we may email you the new songs added since your last visit. If you used to be a Premium member, we may email you once a month about new songs and coming back to Premium. We stop those emails once you have been away for a year. Every one of these emails has an unsubscribe link at the bottom. Use it and the emails stop. We do not sell or share your email address for anyone else's marketing, and we show no ads.",
        "to email Premium members about the DJ Booth: if you are a Premium member, we may email you once a week for up to six weeks to show you the DJ Booth. We stop as soon as you try it. Every one of these emails has two links at the bottom: one stops just the DJ Booth emails, and the other unsubscribes you from all our emails.",
        "to email people who signed up for song emails without a login. We first send one email asking you to confirm, and nothing else goes out until you press its confirm button. After that you get the same song emails as members: new songs, featured songs and a monthly roundup. Every email has an unsubscribe link.",
        "to protect the site from abuse and fix problems",
        "to understand how people use the site and to improve it: how many times each page is opened and each song is played, how many visitors are new, and what members report. We never sell this information or share it for advertising.",
      ]} />

      <H>Who helps us</H>
      <p>We share information only with the companies that run parts of the site for us, and only what they need:</p>
      <List items={[
        "Stripe: payments, sales tax, fraud checks and receipts.",
        "Resend: sending our emails, such as password resets, the link that confirms your email address, the email that asks you to confirm a sign-up for song emails, and a welcome email with tips, emails about songs, such as new songs, featured songs and a monthly roundup, and emails that show Premium members the DJ Booth.",
        "Cloudflare: storing and delivering the music and video files, the site's web address, and a quick check on the sign-up form (Cloudflare Turnstile) that tells people from bots. Turnstile looks at basic information from your browser to do that.",
        "Cloudflare also provides a relay for the DJ Booth. When a friend's phone and the screen cannot reach each other directly, the relay passes the file between them. The file is encrypted from the phone to the screen, and Cloudflare does not keep it. When the two devices connect directly, each can see the other's internet address, as with any direct connection. The short messages that let the two devices find each other pass through our servers and are deleted after 3 minutes.",
        "Our hosting provider and database: where the site and your account information live.",
        "ntfy: a notification service that sends the owner a short alert on his phone when someone opens an account, upgrades, cancels or sends a suggestion. An alert can include what you wrote in a suggestion or cancel reason. It does not include your name or email address unless the owner turns that on.",
        "Google Fonts: the site loads its fonts from Google, so your browser contacts Google to get them.",
      ]} />
      <p>
        We may also share information if the law requires it or to protect the site and people using it. We do not sell your personal information or
        share it for advertising.
      </p>

      <H>Cookies and similar tools</H>
      <p>
        We use only the small pieces of data in your browser that keep you signed in, remember your player settings, and tell us this browser has been
        here before. We do not use advertising cookies. We count page visits ourselves, as described above, and we do not use outside analytics or
        session-recording services.
      </p>

      <H>How long we keep it</H>
      <p>
        We keep your account information while your account is open. Payment and tax records are kept as long as the law and Stripe require.
        The daily page and play counts have no names in them, and we keep them for about a year.
        When you ask us to delete your account, we delete your account details, playlists, favorites and history.
        If you ask for emails without a login and never press the confirm button, we delete your request after 30 days.
      </p>

      <H>Your choices</H>
      <List items={[
        "You can cancel Premium at any time on your Account page.",
        "You can stop all emails about songs, including the monthly roundup and the come-back emails, at any time with the unsubscribe link at the bottom of them or the switch on your Account page. If you do, we keep your email address on a do-not-email list so we do not email you about songs again, even if you delete your account and sign up again. Emails about your account, such as password resets and billing notices, still go out. This works the same if you signed up for emails without a login.",
        "If you are a Premium member, you can stop just the DJ Booth emails, and keep the rest, with the first link at the bottom of those emails or the DJ Booth switch on your Account page. We keep that email address on a separate list so the DJ Booth emails do not start again, even if you delete your account and sign up again. Unsubscribing from all our emails stops the DJ Booth emails too.",
        `You can ask us to show you, correct or delete the information we hold about you by emailing ${CONTACT_EMAIL}.`,
        "Depending on where you live, such as Texas or California, you may have extra privacy rights, including access, correction and deletion. We honor them. We do not sell personal information.",
      ]} />

      <H>Children</H>
      <p>
        The site is not meant for children under 13, and we do not knowingly collect information from them. If you think a child has given us
        information, email us and we will delete it.
      </p>

      <H>Security</H>
      <p>
        We protect your information with reasonable measures, including scrambled passwords and secure connections. No website can promise
        perfect security, so please use a password you do not use anywhere else.
      </p>

      <H>Changes</H>
      <p>
        If we change this policy, we will update the date at the top. If a change is a big one, we will tell you by email or on the site.
      </p>
    </Page>
  );
}
